import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const EnrollmentContext = createContext();

export const useEnrollment = () => {
  const context = useContext(EnrollmentContext);
  if (!context) {
    throw new Error('useEnrollment must be used within an EnrollmentProvider');
  }
  return context;
};

export const EnrollmentProvider = ({ children }) => {
  const { token } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadEnrollments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get('/api/analytics/my-progress');
      // my-progress retourne { stats, enrollments, recentActivity }
      setEnrollments(data.enrollments || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (token) loadEnrollments(); }, [token, loadEnrollments]);

  const getActiveEnrollments = () =>
    enrollments.filter(e => e.status === 'active' || e.progress < 100);

  const getCompletedEnrollments = () =>
    enrollments.filter(e => e.status === 'completed' || e.progress === 100);

  const enrollInCourse = async (courseId) => {
    try {
      const { data } = await axios.post(`/api/courses/${courseId}/enroll`);
      await loadEnrollments(); // Recharge la liste
      return { success: true, enrollment: data.enrollment };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || err.message };
    }
  };

  const isEnrolled = (courseId) =>
    enrollments.some(e => String(e.course_id) === String(courseId) || String(e.courseId) === String(courseId));

  const getEnrollment = (courseId) =>
    enrollments.find(e => String(e.course_id) === String(courseId) || String(e.courseId) === String(courseId)) || null;

  const value = {
    enrollments,
    loading,
    error,
    loadEnrollments,
    getActiveEnrollments,
    getCompletedEnrollments,
    enrollInCourse,
    isEnrolled,
    getEnrollment,
  };

  return (
    <EnrollmentContext.Provider value={value}>
      {children}
    </EnrollmentContext.Provider>
  );
};
